import {SessionService} from "./SessionService/SessionService";
import {IInsights} from "../Contracts/IInsights";
import {getLogger} from "../Services/LoggingService";
import {CheckoutApi} from "./SessionService/CheckoutApi";
import {PosthogAnalytics} from "./InternalAnalytics/PosthogAnalytics";
import {PosthogV2Analytics} from "./InternalAnalytics/PosthogV2Analytics";
import {buildPosthogV2Config} from "./InternalAnalytics/posthogV2Options";
import {GlobalEventBus as eventBus} from "./GlobalEventBus";
import {GtmAnalytics} from "./GtmAnalytics/GtmAnalytics";
import {ABTester} from "./ABTester/ABTester";
import {IABTesterService} from "./ABTester";
import {LocalEnrollmentStorage} from "./ABTester/FrontendExperimentService/LocalEnrollmentStorage";
import {AffiliateLinkService} from "./AffiliateLinkService/AffiliateLinkService";
import {IAffiliateLinkService} from "../Contracts/GloablServices/IAffiliateLinkService";
import {ExpiringLocalStorageService} from "./LocalStorageService/ExpiringLocalStorageService";
import {CookieStorageService} from "./CookieStorageService/CookieStorageService";
import {WidgetsOrchestratorService} from "./GlobalIdService";
import {CartService} from "./CartService";
import {listenToExternalCheckoutWindowEvents} from "../Composables/useCheckoutWindowEvents";
import {TriggerVisibility, TriggerVisibilityService} from "./TriggerVisibilityService";

declare global {
    interface Window {
        // Old widget mounts this object
        BookingKitApp?: any;
        bookingkitServices: {
            cartService: CartService,
            eventBus: typeof eventBus;
            sessionService: SessionService;
            loggerService: ILogger;
            internalAnalyticsService: IInsights;
            internalAnalyticsServiceV2?: IInsights;
            abTester: IABTesterService;
            affiliateLinkService: IAffiliateLinkService;
            widgetOrchestrator: WidgetsOrchestratorService;
            triggerVisibility: TriggerVisibilityService;
        }
    }
}

const loggerService = getLogger();
const sessionService =  new SessionService(
    {
        checkoutApi: new CheckoutApi({
            baseUrl:  import.meta.env.VITE_BK_CHECKOUT_API_URL as string
        }),
        loggerService: loggerService,
        eventBus,
        sessionStorage: new CookieStorageService(60 * 60 * 24) // 1 day, extended on every load
    });

const widgetOrchestrator = new WidgetsOrchestratorService(eventBus);

const postHogConfig= {
    projectId: import.meta.env.VITE_POSTHOG_PROJECT,
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false
}
const internalAnalyticsService = new PosthogAnalytics({
    logger: loggerService,
    eventBus,
    config: postHogConfig
});

// Temporary second PostHog instance (posthog-js 1.418.x, isolated from the bundled one).
// Only constructed -> only fetched -> only initialised when BOTH env vars are set.
// Removal runbook: docs/posthogMigration.md
const postHogV2Config = buildPosthogV2Config(
    import.meta.env.VITE_POSTHOG_V2_PROJECT,
    import.meta.env.VITE_POSTHOG_V2_HOST
);
const internalAnalyticsServiceV2 = postHogV2Config
    ? new PosthogV2Analytics({
        logger: loggerService,
        eventBus,
        config: postHogV2Config,
        // Resolves with the session id minus the server hash; the checkout app derives its PostHog distinct id the same way.
        distinctId: sessionService.getSessionPrefix(),
    })
    : undefined;

const analytics  = new GtmAnalytics({eventBus});
analytics.init();

const abTester = new ABTester(new LocalEnrollmentStorage(), eventBus);

const affiliateLinkService = new AffiliateLinkService(eventBus, new ExpiringLocalStorageService(60 * 60 * 24 * 14)); // 14 days
sessionService.init()
    .catch((e) => loggerService.error(e));

const cartService = new CartService();

listenToExternalCheckoutWindowEvents(eventBus, import.meta.env.VITE_BK_CHECKOUT_BASE_URL as string);

window.bookingkitServices = {
    sessionService,
    eventBus,
    loggerService,
    internalAnalyticsService,
    internalAnalyticsServiceV2,
    abTester,
    affiliateLinkService,
    widgetOrchestrator,
    cartService,
    triggerVisibility: TriggerVisibility
}
